import { useEffectOnce } from 'react-use';
import {
  Box,
  Button,
  ButtonGroup,
  Heading,
  List,
  Modal,
  Paragraph,
  Text,
  TextButton,
  Toast,
  Upsell,
  useToast,
} from '@zillow/constellation-10';
import { Icon } from '@zillow/constellation-10';
import { IconCalendarClockFilled, IconTrendingFilled, IconTrophyFilled } from '@zillow/constellation-icons';
import { PaymentsRequestIcon } from './2PaymentsRequestIcon';
import { useState } from 'react';
import {
  emitPaymentUpsellTipCtaEvent,
  emitPaymentUpsellTipViewEvent,
  emitPaymentUpsellUndoEvent,
} from '../../../../analytics/clickstreamEvents';
import { useApplicationContextOrUndefined } from '../../../../contexts/ApplicationContext';

interface PaymentsUpsellProps {
  onRequestPayments: () => void;
  onUndoRequestPayments: () => void;
}

function PaymentsAndReportsUpsellList() {
  return (
    <List.Root appearance="none">
      <List.Item>
        <Text>
          <Icon>
            <IconCalendarClockFilled />
          </Icon>{' '}
          Zillow reports on time payments to Experian, Equifax, and TransUnion.
        </Text>
      </List.Item>
      <List.Item>
        <Text>
          <Icon>
            <IconTrendingFilled />
          </Icon>{' '}
          You could build your credit in just a few months.
        </Text>
      </List.Item>
      <List.Item>
        <Text>
          <Icon>
            <IconTrophyFilled />
          </Icon>{' '}
          Building credit can help you qualify for future loans and more.
        </Text>
      </List.Item>
    </List.Root>
  );
}

const PAYMENTS_UPSELL_HEADING = 'Build your credit when you pay rent';

export function PaymentsAndReportsUpsellBox(props: React.ComponentProps<typeof Box>) {
  const { css: cssProp, children, ...rest } = props;
  return (
    <Box
      {...rest}
      css={{
        padding: 'default',
        backgroundColor: 'bg.accent.brand.tint',
        borderRadius: 'default',
        ...cssProp,
      }}
    >
      <Heading level={2} textStyle="body-lg-bold">
        {PAYMENTS_UPSELL_HEADING}
      </Heading>
      <PaymentsAndReportsUpsellList />
      <Paragraph css={{ color: 'text.subtle' }}>
        We’ll send a request to your landlord if your application is accepted.
      </Paragraph>
      <Paragraph css={{ color: 'text.subtle' }}>Showing interest now won’t affect your application.</Paragraph>
    </Box>
  );
}

export function PaymentsAndReportsTipUpsell({ onRequestPayments, onUndoRequestPayments }: PaymentsUpsellProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const applicationContext = useApplicationContextOrUndefined();

  useEffectOnce(() => {
    emitPaymentUpsellTipViewEvent(applicationContext);
  });

  return (
    <>
      <Upsell
        tone="hero"
        elevated={false}
        css={{ flexDirection: 'column' }}
        header={
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
            {PAYMENTS_UPSELL_HEADING}
          </Heading>
        }
        media={<PaymentsRequestIcon height={75} />}
        body={<PaymentsAndReportsUpsellList />}
        action={
          <TextButton
            type="button"
            onClick={() => {
              setModalOpen(true);
              emitPaymentUpsellTipCtaEvent(applicationContext);
            }}
          >
            Request Zillow payments
          </TextButton>
        }
      />
      <RequestZillowPaymentsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onRequestPayments={onRequestPayments}
        onUndoRequestPayments={onUndoRequestPayments}
      />
    </>
  );
}

export function RequestZillowPaymentsModal({
  isOpen,
  onClose,
  onRequestPayments,
  onUndoRequestPayments,
}: {
  isOpen: boolean;
  onClose: () => void;
  onRequestPayments: () => void;
  onUndoRequestPayments: () => void;
}) {
  const applicationContext = useApplicationContextOrUndefined();

  const onUndo = () => {
    onUndoRequestPayments();
    emitPaymentUpsellUndoEvent(applicationContext);
    onClose();
  };

  const onOkay = () => {
    onRequestPayments();
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="md"
      header={
        <Heading level={2} fontFamily="heading-alt">
          You’ve requested Zillow payments
        </Heading>
      }
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          <Paragraph>We’ll send a request to your landlord if your application is accepted.</Paragraph>
          <Paragraph>Showing your interest now does not affect your application.</Paragraph>
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Zillow Payment Request actions">
          <TextButton type="button" onClick={onUndo} aria-description="Undo Zillow Payment request">
            Undo
          </TextButton>
          <Button
            tone="brand"
            emphasis="filled"
            type="button"
            onClick={onOkay}
            aria-description="Keep Zillow payments request"
          >
            OK
          </Button>
        </ButtonGroup>
      }
    />
  );
}
interface PaymentRequestToastProps {
  onUndoRequestPayments?: () => void;
}

export function PaymentRequestToast({ onUndoRequestPayments }: PaymentRequestToastProps) {
  const { enqueueToast } = useToast();
  const handleUndoRequestPayments = () => {
    onUndoRequestPayments?.();
    enqueueToast(<Toast tone="success">Request for Zillow payments removed</Toast>);
  };
  return (
    <Toast
      tone="success"
      actionButton={
        <TextButton onImpact onClick={handleUndoRequestPayments}>
          Undo
        </TextButton>
      }
    >
      We’ll send a request to your landlord to report your rent payments to the credit bureaus if your application is
      accepted
    </Toast>
  );
}
